import { Currency } from "@redotech/money/currencies";
import {
  ExchangeVariant,
  InventoryPolicy,
  PendingReturnItem,
  ReturnableItem,
  ReturnProduct,
} from "@redotech/redo-model/draft-return/draft-return-items";
import { getPrimaryProduct } from "@redotech/redo-model/draft-return/util";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import {
  FieldId,
  InputAnswerPartWithStep,
  ReturnFlow,
  ReturnOption,
  Step,
} from "@redotech/redo-model/return-flow";
import {
  getNonAdvancedExchangeType,
  getVariantExchangePriceDifference,
} from "@redotech/redo-model/return-flow/price-difference";
import {
  ReturnOptionMethod,
  StoreCreditType,
} from "@redotech/redo-model/return-flow/return-option";
import { ExchangeGroup } from "@redotech/redo-model/team";
import { ReturnAppSettings } from "../settings";

//For shared code between shop-mini frontend and customer-portal-app frontend
interface ItemStepData {
  flowPath: readonly number[];
  inputAnswers: readonly InputAnswerPartWithStep[];
  multipleChoiceAnswers: readonly {
    step: number;
    answer: number;
    fieldId?: FieldId;
    additionalAiInfo?: string;
  }[];
}

export type CompensationMethodData = Pick<
  PendingReturnItem,
  | "advancedExchangeItems"
  | "exchangeGroup"
  | "variantExchangeItem"
  | "expandedProductVariantExchanges"
  | "quantity"
>;

export type NewReturnItemData = ItemStepData &
  CompensationMethodData & { item: CombinedReturnItem; flow: ReturnFlow };

export enum StepType {
  FLOW = "flow",
  VARIANT_EXCHANGE = "variant-exchange",
  // For shop mini and 4.0
  EXCHANGE_GROUPS = "exchange-groups",
  // For 4.0 web only
  SUGGESTED_EXCHANGE = "suggested-exchange", // Suggested variant exchange or exchange groups
  RECOMMENDED_EXCHANGE = "recommended-exchange", // Exchange recommendations (advanced exchange)
  // for 3.5
  EXCHANGE_GROUP_SELECTED = "exchange-group-selected",
  EXCHANGE_GROUP_SELECTION = "exchange-group-selection",
  BUNDLE_TYPE = "bundle-type",
  ENSO_ENGRAVING = "enso-engraving",
}

export type ItemReturnFlowStep = ItemStepData &
  (
    | {
        type: StepType.FLOW;
        alreadySuggestedExchange?: true;
        preserveSuggestedExchange?: true;
      }
    | { type: StepType.VARIANT_EXCHANGE }
    | {
        type: StepType.EXCHANGE_GROUPS;
        exchangeGroups: ExchangeGroup[];
        suggestedExchangeOptions?: {
          isSuggestedExchange: boolean;
          suggestedVariantOptions?: { name: string; values: string[] }[];
        };
      }
    | { type: StepType.SUGGESTED_EXCHANGE; exchangeGroups: ExchangeGroup[] }
    | { type: StepType.RECOMMENDED_EXCHANGE }
    | { type: StepType.EXCHANGE_GROUP_SELECTED; exchangeGroup: ExchangeGroup }
    | { type: StepType.EXCHANGE_GROUP_SELECTION }
    | { type: StepType.BUNDLE_TYPE; options: ReturnOption[] }
    | { type: StepType.ENSO_ENGRAVING; variant: ExchangeVariant }
  );

export type CombinedReturnItem = ReturnableItem & {
  pendingItem?: PendingReturnItem;
  availableQuantity: number;
};

// Functions for getting return options on return step page

export enum ReturnOptionType {
  SAME_ITEM_EXCHANGE = "same-item-exchange",
  VARIANT_EXCHANGE = "variant-exchange",
  EXCHANGE_GROUPS = "exchange-groups",
  SHOP_OR_RETURN = "shop-or-return",
  BUNDLE_TYPE = "bundle-type",
}

export type ReturnStepPageOption =
  | { type: ReturnOptionType.SHOP_OR_RETURN; options: ReturnOption[] }
  | {
      type: ReturnOptionType.EXCHANGE_GROUPS;
      eligibleExchangeGroups: ExchangeGroup[];
    }
  | { type: ReturnOptionType.VARIANT_EXCHANGE }
  | { type: ReturnOptionType.SAME_ITEM_EXCHANGE; variant: ExchangeVariant }
  | { type: ReturnOptionType.BUNDLE_TYPE; options: ReturnOption[] };

export function getReturnStepPageOptions(
  returnStep: Step.Return,
  product: ReturnProduct,
  settings: ReturnAppSettings,
  returnableItem: ReturnableItem,
  returnType: ReturnTypeEnum | undefined,
): ReturnStepPageOption[] {
  if (returnableItem?.isExpanded && returnableItem.products.length > 1) {
    return [
      { type: ReturnOptionType.BUNDLE_TYPE, options: returnStep.options },
    ];
  }

  const options: ReturnStepPageOption[] = [];
  const exchangeOptions = returnStep.options.filter(
    (option) => option.method.type === ReturnOptionMethod.EXCHANGE,
  );
  const sameItemVariant =
    exchangeOptions.filter((option) => option.exchangeTypes.sameItem).length &&
    getSameItemVariant(product, settings, returnType);

  if (sameItemVariant) {
    options.push({
      type: ReturnOptionType.SAME_ITEM_EXCHANGE,
      variant: sameItemVariant,
    });
  }

  const canDoVariantExchange =
    exchangeOptions.filter((option) => option.exchangeTypes.variant).length &&
    product.variantData.variants.filter(
      (variant) =>
        String(variant.id) !== product.variantId &&
        isVariantInStock({
          settings,
          returnType,
          inventoryPolicy: variant.inventoryPolicy,
          inventoryTracked: variant.inventoryTracked,
          inventoryQuantity: variant.inventoryQuantity,
        }),
    ).length >= 1;

  if (canDoVariantExchange) {
    options.push({ type: ReturnOptionType.VARIANT_EXCHANGE });
  }

  const exchangeGroupsAllowed = exchangeOptions.some(
    (option) => option.exchangeTypes.exchangeGroups,
  );
  if (exchangeGroupsAllowed) {
    const eligibleExchangeGroups = getEligibleExchangeGroupsForProduct(
      product,
      settings.exchangeGroups,
    );
    if (eligibleExchangeGroups.length > 0) {
      options.push({
        type: ReturnOptionType.EXCHANGE_GROUPS,
        eligibleExchangeGroups,
      });
    }
  }

  const otherReturnOptions = getRefundReturnOptions(returnStep);

  if (otherReturnOptions.length) {
    options.push({
      type: ReturnOptionType.SHOP_OR_RETURN,
      options: otherReturnOptions,
    });
  }

  if (options.length === 0 && returnStep.options[0]) {
    // something went wrong
    // either all exchange options are out of stock
    // or the return flow is misconfigured

    // give the shopper the option to return for store credit.
    options.push({
      type: ReturnOptionType.SHOP_OR_RETURN,
      options: [
        {
          ...returnStep.options[0],
          method: {
            type: ReturnOptionMethod.STORE_CREDIT,
            creditType: StoreCreditType.GIFT_CARD,
          },
        },
      ],
    });
  }

  return options;
}

export function getNonExchangeReturnOptions(
  flowStep: Step.Return,
  product?: ReturnProduct,
  settings?: ReturnAppSettings,
  returnItem?: ReturnableItem,
  returnType?: ReturnTypeEnum,
) {
  if (!product || !settings || !returnItem) {
    return [];
  }
  return getReturnStepPageOptions(
    flowStep,
    product,
    settings,
    returnItem,
    returnType,
  ).filter(
    (option) =>
      option.type !== ReturnOptionType.VARIANT_EXCHANGE &&
      option.type !== ReturnOptionType.SAME_ITEM_EXCHANGE &&
      option.type !== ReturnOptionType.EXCHANGE_GROUPS,
  );
}

export function getRefundReturnOptions(returnStep: Step.Return) {
  return returnStep.options.filter(
    (option) =>
      option.method.type !== ReturnOptionMethod.EXCHANGE ||
      option.exchangeTypes.advanced,
  );
}

// This will generate the same item variant for a return if the same item exchange option is available
// For a shipping claim or warranty claim, same item exchange is the only exchange option available
export function getSameItemVariant(
  product: ReturnProduct,
  settings: ReturnAppSettings,
  returnType: ReturnTypeEnum | undefined,
) {
  return product.variantData.variants.find(
    (variant) =>
      String(variant.id) === product.variantId &&
      isVariantInStock({
        settings,
        returnType,
        inventoryPolicy: variant.inventoryPolicy,
        inventoryTracked: variant.inventoryTracked,
        inventoryQuantity: variant.inventoryQuantity,
      }),
  );
}

/**
 * This function is used to check if a variant's properties allow it to be "purchased" via
 * exchange.
 *
 * There are two types of InventoryPolicy enum, one in the draft return model and one in
 * the product model.
 * The product model enum is lowercase and the draft return model enum is uppercase. This
 * function only accepts the draft return model enum.
 * @param settings - The settings object.
 * @param returnType - The type of return.
 * @param availableForSale - Whether the variant is available for sale. This is populated
 *   on the ExchangeProductVariant object on the frontend, and is actually a representation
 *   of this function's return value.
 * @param inventoryPolicy - The inventory policy of the variant. Based on the draft return
 *   model enum.
 * @param inventoryTracked - Whether the variant is tracked. Essentially another ignore
 *   inventory argument, but derived from the shopify product.
 * @param inventoryQuantity - The quantity of the variant.
 * @param ignoreStock - Whether to ignore the stock.
 * @returns Whether the variant can be "purchased" via exchange.
 */
export const isVariantInStock = ({
  settings,
  returnType = ReturnTypeEnum.RETURN,
  availableForSale = false,
  inventoryPolicy = InventoryPolicy.DENY,
  inventoryTracked = true,
  inventoryQuantity = 0,
  ignoreStock = false,
}: {
  settings?: ReturnAppSettings;
  returnType?: ReturnTypeEnum;
  availableForSale?: boolean;
  inventoryPolicy?: InventoryPolicy;
  inventoryTracked?: boolean;
  inventoryQuantity?: number;
  ignoreStock?: boolean;
}) => {
  if (!settings) {
    return false;
  }
  const ignoreDueToArgs = ignoreStock;

  const ignoreBecauseInventoryIsNotTracked = !inventoryTracked;

  const ignoreAccordingToTeamSettings = settings.inventory.ignoreInventory;

  const ignoreAccordingToReturnType =
    settings.inventory.ignoreInventoryForReturnTypes.includes(returnType);

  const ignoreAccordingToInventoryPolicy =
    settings.inventory.followShopifyInventoryPolicy &&
    // Even though this function should only accept the draft return model enum,
    // we are converting to lowercase to be safe.
    inventoryPolicy.toLowerCase() === InventoryPolicy.CONTINUE.toLowerCase();

  const ignoreAccordingToAvailability =
    settings.inventory.followShopifyInventoryPolicy && availableForSale;

  const quantityAboveMinimumStock =
    (inventoryQuantity || 0) >= settings.inventory.minimum_stock;

  return (
    ignoreDueToArgs ||
    ignoreBecauseInventoryIsNotTracked ||
    ignoreAccordingToTeamSettings ||
    ignoreAccordingToReturnType ||
    ignoreAccordingToInventoryPolicy ||
    ignoreAccordingToAvailability ||
    quantityAboveMinimumStock
  );
};
export function exchangeVariantToVariantData(
  variant: ExchangeVariant,
  appSettings: ReturnAppSettings,
  product: ReturnProduct,
  returnType?: ReturnTypeEnum,
  currency?: Currency | null,
) {
  return {
    variantId: variant.id,
    variantTitle: variant.title,
    price: { amount: variant.price, currency: currency ?? Currency.USD },
    image: { url: variant.image ?? product.image, altText: variant.title },
    isInStock: isVariantInStock({
      settings: appSettings,
      returnType,
      inventoryQuantity: variant.inventoryQuantity,
      inventoryPolicy: variant.inventoryPolicy,
      inventoryTracked: variant.inventoryTracked,
    }),
    onlineInventoryAvailable: variant.inventoryQuantity,
    options: variant.selectedOptions,
  };
}

export function getReturnedProductVariantData(
  product: ReturnProduct,
  settings: ReturnAppSettings,
  returnType?: ReturnTypeEnum,
  currency?: Currency | null,
) {
  const originalVariant = product!.variantData.variants.find(
    (variant) => variant.id === product?.variantId,
  );

  return originalVariant
    ? exchangeVariantToVariantData(
        originalVariant,
        settings,
        product!,
        returnType,
        currency,
      )
    : undefined;
}

export function getEligibleExchangeGroupsForProduct(
  product: ReturnProduct,
  exchangeGroups: ExchangeGroup[] | undefined,
): ExchangeGroup[] {
  return (
    exchangeGroups?.filter((group) => {
      switch (group.type) {
        case "tag:tag":
        case "tag:collection": {
          return group.sourceTags.find((tag) =>
            product.variantData.tags.includes(tag),
          );
        }
        case "name:collection":
          return product.variantData.productTitle
            .toLowerCase()
            .includes(group.sourceName.toLowerCase());
        case "collection:collection":
          return product.collections.includes(group.sourceCollection.name);
        default:
          return false;
      }
    }) || []
  );
}

/** When a "group" of options will reduce to exactly one option and that has a bonus/fee */
export type GroupedReturnOptionsBadge = {
  badgeText: string;
  pillTheme: PillTheme;
};

export function getGroupedReturnOptionsButtonText(
  returnOptions: ReturnOption[],
): { text: string; badge?: GroupedReturnOptionsBadge } {
  const { exchange, refund, credit } = getReturnOptionBools(returnOptions);

  // Start with the base text
  let str = exchange ? "Exchange for" : "Return for";
  let delim = "";

  if (exchange) {
    str += " a new product";
    delim = "or get";
  }
  if (refund) {
    str += ` ${delim} a refund`;
    delim = "or";
  }
  if (credit && (!refund || !exchange)) {
    str += ` ${delim} store credit`;
  }

  // Add incentive/fee information if there's only one return option with an adjustment
  let bonus = undefined;
  if (returnOptions.length === 1 && returnOptions[0]?.adjustment) {
    const adj = returnOptions[0].adjustment.flat;
    if (adj !== 0) {
      // Use absolute value for display and determine if it's a bonus or fee
      const absAdj = Math.abs(adj);
      const formattedAdj = `$${absAdj.toFixed(2)}`;

      if (adj > 0) {
        bonus = {
          badgeText: `${formattedAdj} Bonus`,
          pillTheme: PillTheme.SUCCESS,
        };
      } else if (adj < 0) {
        bonus = {
          badgeText: `${formattedAdj} Fee`,
          pillTheme: PillTheme.DANGER,
        };
      }
    }
  }

  // Remove double spaces
  str = str.replace(/\s+/g, " ");
  return { text: str, badge: bonus };
}

function getReturnOptionBools(returnOptions: ReturnOption[]): {
  exchange: boolean;
  refund: boolean;
  credit: boolean;
} {
  let exchange = false;
  let refund = false;
  let credit = false;

  for (const option of returnOptions) {
    if (option.method.type === ReturnOptionMethod.EXCHANGE) {
      exchange = true;
      continue;
    }
    if (option.method.type === ReturnOptionMethod.STORE_CREDIT) {
      credit = true;
      continue;
    }
    if (option.method.type === ReturnOptionMethod.REFUND) {
      refund = true;
      continue;
    }
  }
  return { exchange, refund, credit };
}

export const isEnsoEngraving = (
  settings: ReturnAppSettings,
  product: ReturnProduct,
  returnType: ReturnTypeEnum,
): boolean => {
  const someVariantsAvailable = !!product.variantData.variants.some((variant) =>
    isVariantInStock({
      settings,
      returnType,
      inventoryPolicy: variant.inventoryPolicy,
      inventoryTracked: variant.inventoryTracked,
      inventoryQuantity: variant.inventoryQuantity,
    }),
  );
  const sameVariantAvailable = !!product.variantData.variants.find(
    (variant) =>
      String(variant.id) === String(product.variantId) &&
      isVariantInStock({
        settings,
        returnType,
        inventoryPolicy: variant.inventoryPolicy,
        inventoryTracked: variant.inventoryTracked,
        inventoryQuantity: variant.inventoryQuantity,
      }),
  );
  return (
    settings.storeUrl === "ensorings.myshopify.com" /* 😡 */ &&
    product.productProperties?.text != null &&
    ((someVariantsAvailable && returnType === ReturnTypeEnum.RETURN) ||
      (sameVariantAvailable && returnType === ReturnTypeEnum.CLAIM))
  );
};

export const ENSORINGS_ENGRAVING_ID_PAID = "41268244152433";
export const ENSORINGS_ENGRAVING_ID_FREE = "41268244185201";
export const ENSO_RING_ENGRAVING_FEE = "8.99";

export function shouldApplyPriceDifferenceForExchangeVariant(
  returnableItem: ReturnableItem | null | undefined,
  exchangeVariantId: string | null | undefined,
  settings?: ReturnAppSettings,
  exchangeGroup?: ExchangeGroup | null,
): boolean {
  if (!returnableItem) {
    return true;
  }

  const primaryProduct = getPrimaryProduct(returnableItem);
  const exchangeType = getNonAdvancedExchangeType(
    primaryProduct.variantId,
    exchangeVariantId,
    exchangeGroup,
  );
  const shouldApplyPriceDifference =
    getVariantExchangePriceDifference(
      exchangeType,
      primaryProduct,
      settings?.exchanges.differentPricedVariantExchanges ?? false,
      settings?.exchanges.variantExchangeProductTags ?? [],
    ) === "apply";

  return shouldApplyPriceDifference;
}
